import { createSlice } from "@reduxjs/toolkit"
import type { AgeFilterOption } from "components/AgeFilter"

type OnboardingsFormValue = {
    statusId: any
    dealType: string
    age: number[]
    age_op: AgeFilterOption | null
    assigned_to: Record<string, any>
    tags: any
    TicketStartDate: string
    TicketEndDate: string
    parent_status: string
    csms_partner_name: Record<string, any>
    cpo_account_name: any[]
    category_ids: { id: number; title: string }[]
    on_hold: Record<string, any>
    search_key: string
    site_id?: string[]
}
type AddOnboardingFormValue = {
    // CPO page
    cpo_account_name: string
    cpo_legal_name: string
    billing_address_id: number | null
    billing_address: string
    country: string
    state: string
    city: string
    pincode: string
    gstin: string
    contacts_name: string | null
    contacts: any[]
    company_invoice_required: string
    bank_detail: any | null
    beneficiary_name: string
    bank_name: any | null
    account_number: string
    re_account_number: string
    ifsc_code: string
    account_type: any | null
    pan_number: string
    authorised_signature: string
    escalation_percentage: string
    per_unit_charger: string
    ac_cost: string
    dc_cost: string

    // Franchise page
    franchise_name: string
    franchise_billing_address_id: number | null
    franchise_billing_address: string
    franchise_country: string
    franchise_state: string
    franchise_city: string
    franchise_pincode: string
    franchise_gstin: string
    franchise_contacts_name: string | null
    franchise_contacts: any[]
    franchise_bank_detail: any | null
    franchise_beneficiary_name: string
    franchise_bank_name: any | null
    franchise_account_number: string
    franchise_re_account_number: string
    franchise_ifsc_code: string
    franchise_account_type: any | null
    franchise_pan_number: string
    franchise_is_saas_fee_applicable: number | null
    franchise_saas_fee_ac: string
    franchise_saas_fee_dc: string
    franchise_host: any | null
    franchise_tenure: string
    franchise_agreement_start_date: string
    franchise_agreement_end_date: string
    franchise_agreement: string
    franchise_agreement_url: string

    // Host page
    host_account_name: string
    host_legal_name: string
    host_rent: string
    host_profit_share: string
    host_billing_address: any | null
    host_billing_address_id: any | null
    host_country: string
    host_state: string
    host_city: string
    host_pincode: string
    host_gstin: string
    host_contacts_name: string | null
    host_contacts: any[]
    rent_amount: string
    incremental: string
    incremental_duration: string
    incremental_percentage: string
    host_agreement: string
    host_chargers: any[]

    // Station page
    station_id: number | null
    station_name: string
    site_name: string
    station_address: string
    station_country: any | null
    station_state: any | null
    station_city: any | null
    station_pincode: string
    station_latitude: string
    station_longitude: string
    station_landmark: string
    opening_hours: string
    custom_time_start: string
    custom_time_end: string
    location_type: any | null
    location_subType: any | null
    amenities: any | null
    access_type: any | null
    co_branded: any | null
    co_branded_toggle: string
    vehicle_type: any[]
    parking_type: any | null
    parking_slots: string

    // Charger page
    chargers: any[]
}
type InitialState = {
    users: any[]
    totalPages: number
    page: number
    selectedValue: number
    selectedTab: number
    currentRules: any[]
    displayValues: any[]
    selectedMetric: any[]
    metricExplicitlyCleared: boolean
    OnboardingFormValue: OnboardingsFormValue
    visibleColumns: { name: string; visible: boolean }[]
    addOnboardingFormValue: AddOnboardingFormValue
}

const initialState: InitialState = {
    users: [],
    totalPages: 1,
    page: 1,
    selectedValue: 10,
    displayValues: [],
    currentRules: [],
    OnboardingFormValue: {
        statusId: [],
        dealType: "",
        age: [],
        age_op: null,
        assigned_to: {},
        tags: [],
        TicketStartDate: "",
        TicketEndDate: "",
        on_hold: {},
        csms_partner_name: {},
        cpo_account_name: [],
        category_ids: [],
        search_key: "",
        parent_status: "all_tab",
    },
    selectedMetric: [],
    metricExplicitlyCleared: false,
    selectedTab: 0,
    visibleColumns: [
        { name: "Raised On", visible: false },
        { name: "Raised By", visible: false },
        { name: "CSMS Company Name", visible: false },
    ],
    addOnboardingFormValue: {
        // CPO page
        cpo_account_name: "",
        cpo_legal_name: "",
        billing_address_id: null,
        billing_address: "",
        country: "",
        state: "",
        city: "",
        pincode: "",
        gstin: "",
        contacts_name: null,
        contacts: [],
        company_invoice_required: "",
        bank_detail: null,
        beneficiary_name: "",
        bank_name: null,
        account_number: "",
        re_account_number: "",
        ifsc_code: "",
        account_type: null,
        pan_number: "",
        authorised_signature: "",
        escalation_percentage: "",
        per_unit_charger: "",
        ac_cost: "",
        dc_cost: "",

        // Franchise page
        franchise_name: "",
        franchise_billing_address_id: null,
        franchise_billing_address: "",
        franchise_country: "",
        franchise_state: "",
        franchise_city: "",
        franchise_pincode: "",
        franchise_gstin: "",
        franchise_contacts_name: null,
        franchise_contacts: [],
        franchise_bank_detail: null,
        franchise_beneficiary_name: "",
        franchise_bank_name: null,
        franchise_account_number: "",
        franchise_re_account_number: "",
        franchise_ifsc_code: "",
        franchise_account_type: null,
        franchise_pan_number: "",
        franchise_is_saas_fee_applicable: null,
        franchise_saas_fee_ac: "",
        franchise_saas_fee_dc: "",
        franchise_host: null,
        franchise_tenure: "",
        franchise_agreement_start_date: "",
        franchise_agreement_end_date: "",
        franchise_agreement: "",
        franchise_agreement_url: "",

        // Host page
        host_account_name: "",
        host_legal_name: "",
        host_rent: "",
        host_profit_share: "",
        host_billing_address: null,
        host_billing_address_id: null,
        host_country: "",
        host_state: "",
        host_city: "",
        host_pincode: "",
        host_gstin: "",
        host_contacts_name: null,
        host_contacts: [],
        rent_amount: "",
        incremental: "",
        incremental_duration: "",
        incremental_percentage: "",
        host_agreement: "",
        host_chargers: [],

        // Station page
        station_id: null,
        station_name: "",
        site_name: "",
        station_address: "",
        station_country: null,
        station_state: null,
        station_city: null,
        station_pincode: "",
        station_latitude: "",
        station_longitude: "",
        station_landmark: "",
        opening_hours: "",
        custom_time_start: "",
        custom_time_end: "",
        location_type: null,
        location_subType: null,
        amenities: null,
        access_type: null,
        co_branded: null,
        co_branded_toggle: "",
        vehicle_type: [],
        parking_type: null,
        parking_slots: "",

        //charger page
        chargers: [],
    },
}

const OnboardingsSlice = createSlice({
    name: "Onboardings",
    initialState,
    reducers: {
        setOnboardingsForm: (state: any, payload: any) => {
            state.OnboardingFormValue = payload?.payload
        },
        setSiteIdFilter: (state, { payload }) => {
            state.OnboardingFormValue.site_id = payload
        },
        setPages: (state, action) => {
            state.page = action?.payload
        },
        setSelectedValues: (state, action) => {
            state.selectedValue = action?.payload
        },
        setSelectedTab: (state, action) => {
            state.selectedTab = action?.payload
        },
        setDisplayValues: (state: any, payload: any) => {
            state.displayValues = payload?.payload
        },
        setCurrentRuleValue: (state: any, payload: any) => {
            state.currentRules = payload?.payload
        },
        setSelectedMetric: (state, action) => {
            state.selectedMetric = action?.payload
        },
        setVisibleColumns: (state, action) => {
            state.visibleColumns = action?.payload
        },
        setMetricExplicitlyCleared: (state, action) => {
            state.metricExplicitlyCleared = action.payload
        },
        setAddOnboardingForm: (state: any, payload: any) => {
            state.addOnboardingFormValue = payload?.payload
        },
        resetFiltersAndMetrics: state => {
            state.OnboardingFormValue = initialState.OnboardingFormValue
            state.selectedMetric = []
            state.metricExplicitlyCleared = false
            state.displayValues = []
            state.currentRules = []
            state.page = 1
        },
    },
})

export const {
    setPages,
    setSelectedValues,
    setSelectedTab,
    setVisibleColumns,
    setOnboardingsForm,
    setDisplayValues,
    setCurrentRuleValue,
    setSelectedMetric,
    setMetricExplicitlyCleared,
    setAddOnboardingForm,
    resetFiltersAndMetrics,
    setSiteIdFilter,
} = OnboardingsSlice.actions
export const initialOnboardingsState = initialState
export const getAllUser = (state: any) => state.users.loading
export const addUserSuccess = (state: any) => state.users.addSuccess

export default OnboardingsSlice.reducer
